import { forwardRef, Ref, useState, ReactElement, useEffect } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  Slide,
  Autocomplete,
  Divider,
  CircularProgress,
  Grid
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import LoadingButton from '@mui/lab/LoadingButton';
import { ptBR } from 'date-fns/locale';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useGetAllRegionsQuery } from 'src/service/slices/regionSlice';
import { useSearchVacanciesMutation } from 'src/service/slices/vacancySlice';
import { useGetAllClientsQuery } from 'src/service/slices/clientSlice';
import { useGetAllUsersQuery } from 'src/service/slices/userSlice';
import { StatusEnum, SearchVacancyDto } from 'src/types/Vacancy';
import { format, isValid } from 'date-fns';

const statusLabels: Record<StatusEnum, string> = {
  [StatusEnum.Open]: 'Aberta',
  [StatusEnum.Sourcing]: 'Em Procura',
  [StatusEnum.AwaitingApprovalClient]: 'Aguardando Aprovação do Cliente',
  [StatusEnum.InDocumentation]: 'Em Documentação',
  [StatusEnum.AwaitingDPApproval]: 'Aguardando Aprovação DP',
  [StatusEnum.Completed]: 'Finalizada',
  [StatusEnum.Cancelled]: 'Cancelada'
};
import { setRecords, setLoading } from 'src/service/slices/searchSlice';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogWrapper = styled(Dialog)(
  () => `
    .MuiDialog-container {
        height: auto;
    }
    
    .MuiDialog-paperScrollPaper {
        max-height: calc(100vh - 64px)
    }
`
);

const SearchInputWrapper = styled(TextField)(
  ({ theme }) => `
    background: ${theme.colors.alpha.white[100]};

    .MuiInputBase-input {
        font-size: ${theme.typography.pxToRem(17)};
    }
`
);

const DialogTitleWrapper = styled(DialogTitle)(
  ({ theme }) => `
    background: ${theme.colors.alpha.black[5]};
`
);

function HeaderSearch() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { data: regionResult } = useGetAllRegionsQuery();
  const { data: clientResult } = useGetAllClientsQuery();
  const { data: userResult } = useGetAllUsersQuery();
  const [searchVacancies, { isLoading: isFetching }] = useSearchVacanciesMutation();

  const [codeValue, setCodeValue] = useState('');
  const [regionValue, setRegionValue] = useState(null);
  const [clientValue, setClientValue] = useState(null);
  const [statusValue, setStatusValue] = useState(null);
  const [userValue, setUserValue] = useState(null);
  const [recordAtValue, setRecordAtValue] = useState(null);
  const [recordEndValue, setRecordEndValue] = useState(null);
  const [error, setError] = useState('');

  const [openAutoRegion, setOpenAutoRegion] = useState(false);
  const [openAutoClient, setOpenAutoClient] = useState(false);
  const [openAutoStatus, setOpenAutoStatus] = useState(false);
  const [openAutoUser, setOpenAutoUser] = useState(false);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    dispatch(setLoading(isFetching));
  }, [isFetching]);

  const handleCodeChange = (event) => {
    const value = event.target.value;
    // Aceita apenas números
    if (value === '' || /^\d+$/.test(value)) {
      setCodeValue(value);
    }
  };

  const handleRegionChange = (event, value) => {
    setRegionValue(value);
    setOpenAutoRegion(false);
  };

  const handleClientChange = (event, value) => {
    setClientValue(value);
    setOpenAutoClient(false);
  };

  const handleStatusChange = (event, value) => {
    setStatusValue(value);
    setOpenAutoStatus(false);
  };

  const handleUserChange = (event, value) => {
    setUserValue(value);
    setOpenAutoUser(false);
  };

  const handleDateChange = (date) => {
    if (date && isValid(date)) {
      setRecordAtValue(date);
      setError(null);
    } else {
      setError('Data Inválida');
      setRecordAtValue(null);
    }
  };

  const handleDateEndChange = (date) => {
    if (date && isValid(date)) {
      setRecordEndValue(date);
      setError(null);
    } else {
      setError('Data Inválida');
      setRecordEndValue(null);
    }
  };

  const searchButton = async () => {
    const searchPayload: SearchVacancyDto = {};

    if (codeValue && codeValue.trim() !== '') searchPayload.code = parseInt(codeValue);
    if (regionValue) searchPayload.regionId = regionValue.id;
    if (clientValue) searchPayload.clientId = clientValue.id;
    if (userValue) searchPayload.userId = userValue.id;
    if (statusValue) searchPayload.status = statusValue;
    if (recordAtValue) searchPayload.startDateAt = format(recordAtValue, 'yyyy-MM-dd');
    if (recordEndValue) searchPayload.endDateAt = format(recordEndValue, 'yyyy-MM-dd');

    // Verifica se há pelo menos um filtro selecionado
    if (Object.keys(searchPayload).length === 0) return;

    try {
      const response = await searchVacancies(searchPayload);

      if ('data' in response) {
        if (response.data.length === 0) {
          enqueueSnackbar('Não foram encontradas vagas!', {
            variant: 'warning'
          });
          dispatch(setRecords([]));
          return;
        }

        dispatch(setRecords(response.data));
        handleClose();
        enqueueSnackbar(`Foram encontradas ${response.data.length} vagas.`, {
          variant: 'success'
        });
        navigate('/search');
      } else {
        enqueueSnackbar('Erro ao buscar vagas. Tente novamente.', {
          variant: 'error'
        });
      }
    } catch (error) {
      enqueueSnackbar('Erro ao buscar vagas. Tente novamente.', {
        variant: 'error'
      });
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Tooltip arrow title="Search">
        <IconButton color="secondary" onClick={handleClickOpen}>
          <SearchTwoToneIcon />
        </IconButton>
      </Tooltip>

      <DialogWrapper
        open={open}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="md"
        fullWidth
        scroll="paper"
        onClose={handleClose}
      >
        <DialogTitleWrapper>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }} mb={1}>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Grid container spacing={2}>
            {/* Primeira linha: Cliente e Usuário */}
            <Grid item xs={12} md={6} lg={6}>
              <Autocomplete
                open={openAutoClient}
                onOpen={() => setOpenAutoClient(true)}
                onClose={() => setOpenAutoClient(false)}
                value={clientValue}
                onChange={handleClientChange}
                options={clientResult ? clientResult : []}
                getOptionLabel={(option) => option.description}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                id="disable-close-on-select"
                disableCloseOnSelect
                renderInput={(params) => (
                  <SearchInputWrapper
                    {...params}
                    label="Cliente"
                    placeholder="Procure por cliente..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Autocomplete
                open={openAutoUser}
                onOpen={() => setOpenAutoUser(true)}
                onClose={() => setOpenAutoUser(false)}
                value={userValue}
                onChange={handleUserChange}
                options={[
                  { id: '00000000-0000-0000-0000-000000000000', firstName: '', lastName: '', userName: 'Usuário Padrão' },
                  ...(userResult || [])
                ]}
                getOptionLabel={(option) => {
                  if (option.id === '00000000-0000-0000-0000-000000000000') {
                    return 'Usuário Padrão';
                  }
                  return `${option.firstName || ''} ${option.lastName || ''}`.trim() || option.userName || 'Usuário';
                }}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                id="disable-close-on-select"
                disableCloseOnSelect
                renderInput={(params) => (
                  <SearchInputWrapper
                    {...params}
                    label="Usuário"
                    placeholder="Procure por usuário..."
                  />
                )}
              />
            </Grid>
            
            {/* Segunda linha: Status e Região */}
            <Grid item xs={12} md={6} lg={6}>
              <Autocomplete
                open={openAutoStatus}
                onOpen={() => setOpenAutoStatus(true)}
                onClose={() => setOpenAutoStatus(false)}
                value={statusValue}
                onChange={handleStatusChange}
                options={Object.values(StatusEnum)}
                getOptionLabel={(option) => statusLabels[option] || option}
                isOptionEqualToValue={(option, value) => option === value}
                id="disable-close-on-select"
                disableCloseOnSelect
                renderInput={(params) => (
                  <SearchInputWrapper
                    {...params}
                    label="Status da Vaga"
                    placeholder="Procure por status..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Autocomplete
                open={openAutoRegion}
                onOpen={() => setOpenAutoRegion(true)}
                onClose={() => setOpenAutoRegion(false)}
                value={regionValue}
                onChange={handleRegionChange}
                options={regionResult ? regionResult : []}
                getOptionLabel={(option) => option.description}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                id="disable-close-on-select"
                disableCloseOnSelect
                renderInput={(params) => (
                  <SearchInputWrapper
                    {...params}
                    label="Região da Vaga"
                    placeholder="Procure por região da vaga..."
                  />
                )}
              />
            </Grid>
            
            {/* Terceira linha: Datas */}
            <Grid item xs={12} md={6} lg={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
                <DatePicker
                  label="Data Inicial"
                  value={recordAtValue}
                  onChange={handleDateChange}
                  renderInput={(params) => (
                    <SearchInputWrapper
                      {...params}
                      fullWidth
                      error={!!error}
                      helperText={error}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
                <DatePicker
                  label="Data Final"
                  value={recordEndValue}
                  onChange={handleDateEndChange}
                  renderInput={(params) => (
                    <SearchInputWrapper
                      {...params}
                      fullWidth
                      error={!!error}
                      helperText={error}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            
            {/* Quarta linha: Código */}
            <Grid item xs={12} md={12} lg={12}>
              <SearchInputWrapper
                label="Código da Vaga"
                placeholder="Digite o código..."
                value={codeValue}
                onChange={handleCodeChange}
                type="number"
                fullWidth
                inputProps={{
                  min: 0,
                  step: 1
                }}
              />
            </Grid>
          </Grid>
        </DialogTitleWrapper>
        <Divider />
        <LoadingButton
          sx={{ margin: 1 }}
          loading={isFetching}
          variant="contained"
          color="primary"
          startIcon={<SearchTwoToneIcon />}
          onClick={searchButton}
          loadingIndicator={<CircularProgress size={24} color="primary" />}
        >
          {' '}
          Buscar{' '}
        </LoadingButton>
      </DialogWrapper>
    </>
  );
}

export default HeaderSearch;
